/**
 * Compact floating voice conversation widget.
 *
 * Renders a small fixed card (bottom-right) instead of a full-screen overlay.
 * Shows a pulsating indicator for the active state, waveform visualisation,
 * and a scrolling transcript — without covering the page.
 */

import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X, Mic } from "lucide-react";
import { useVoiceSession, type VoiceState, type VoiceTranscript } from "@/hooks/useVoiceSession";
import { Waveform } from "./Waveform";
import { cn } from "@/lib/utils";

interface VoiceOverlayProps {
  onClose: () => void;
  /** Called whenever voice transcripts change — lets CoachChat sync them into the chat. */
  onTranscriptUpdate?: (transcripts: VoiceTranscript[]) => void;
}

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function stateLabel(state: VoiceState): string {
  switch (state) {
    case "connecting":
      return "Connecting…";
    case "listening":
      return "Listening";
    case "thinking":
      return "Thinking…";
    case "speaking":
      return "Speaking";
    case "error":
      return "Error";
    default:
      return "";
  }
}

// Ring colour by state
function ringClass(state: VoiceState): string {
  switch (state) {
    case "listening":
      return "border-accent shadow-[0_0_20px_hsl(var(--accent)/0.3)]";
    case "thinking":
      return "border-muted-foreground/40 animate-spin";
    case "speaking":
      return "border-coach shadow-[0_0_20px_hsl(var(--coach)/0.3)]";
    case "connecting":
      return "border-muted-foreground/20 animate-pulse";
    default:
      return "border-border";
  }
}

export function VoiceOverlay({ onClose, onTranscriptUpdate }: VoiceOverlayProps) {
  const {
    state,
    transcripts,
    error,
    startSession,
    endSession,
    inputAnalyser,
    outputAnalyser,
    elapsed,
    isOffline,
  } = useVoiceSession();

  const transcriptEndRef = useRef<HTMLDivElement>(null);

  // Auto-start session when overlay mounts (short delay to avoid Strict Mode double-mount race)
  useEffect(() => {
    const t = setTimeout(() => {
      void startSession();
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync transcripts up to parent (CoachChat) for chat message display
  useEffect(() => {
    if (onTranscriptUpdate && transcripts.length > 0) {
      onTranscriptUpdate(transcripts);
    }
  }, [transcripts, onTranscriptUpdate]);

  // Auto-scroll transcript
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [transcripts]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    endSession();
    onClose();
  };

  const isActive = state === "listening" || state === "thinking" || state === "speaking";

  // Teal / amber for waveform canvas colours
  const TEAL = "0, 255, 200";
  const AMBER = "255, 180, 60";

  const widget = (
    <div
      className={cn(
        "fixed bottom-[calc(1.5rem+4rem+env(safe-area-inset-bottom,0px))] sm:bottom-6 right-6 z-50",
        "w-80 max-w-[90vw] rounded-xl",
        "bg-card/95 backdrop-blur-md border border-border/50 shadow-2xl",
        "flex flex-col overflow-hidden",
        "animate-scale-in",
      )}
      role="dialog"
      aria-modal={false}
      aria-label="Voice conversation"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/50">
        <div className="flex items-center gap-2">
          {/* Pulsating indicator */}
          <div className="relative flex items-center justify-center">
            <div
              className={cn(
                "w-8 h-8 rounded-full border-2 transition-all duration-500 flex items-center justify-center",
                ringClass(state),
              )}
              style={{ animationDuration: state === "thinking" ? "3s" : "2s" }}
            >
              {isActive && (
                <div
                  className={cn(
                    "absolute inset-0 rounded-full animate-ping opacity-20",
                    state === "listening" && "bg-accent",
                    state === "speaking" && "bg-coach",
                    state === "thinking" && "bg-muted-foreground",
                  )}
                  style={{ animationDuration: "2s" }}
                />
              )}
              <Mic className="h-3.5 w-3.5 text-foreground/70" />
            </div>
          </div>

          <div className="flex flex-col">
            <span className="text-xs font-medium text-foreground/80 flex items-center gap-1.5">
              {stateLabel(state) || "Voice"}
              {isOffline && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-mono">
                  Demo
                </span>
              )}
            </span>
            {isActive && (
              <span className="text-[10px] font-mono text-muted-foreground tracking-wider">
                {formatElapsed(elapsed)}
              </span>
            )}
          </div>
        </div>

        <button
          onClick={handleClose}
          className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
          aria-label="End voice conversation"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Waveform area */}
      <div className="flex items-center justify-center px-4 py-3 min-h-[56px]">
        {state === "listening" && (
          <Waveform analyser={inputAnalyser} color={TEAL} active width={240} height={36} />
        )}
        {state === "speaking" && (
          <Waveform analyser={outputAnalyser} color={AMBER} active width={240} height={36} />
        )}
        {state === "thinking" && (
          <div className="flex gap-1.5 items-center">
            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: "0ms" }} />
            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: "150ms" }} />
            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: "300ms" }} />
          </div>
        )}
        {state === "connecting" && (
          <div className="w-5 h-5 border-2 border-muted-foreground/30 border-t-muted-foreground/70 rounded-full animate-spin" />
        )}
      </div>

      {/* Transcript */}
      {transcripts.some((t) => t.text.trim()) && (
        <div className="border-t border-border/50 px-4 py-2 max-h-[160px] overflow-y-auto scrollbar-thin scrollbar-thumb-border">
          <div className="space-y-2">
            {transcripts
              .filter((t) => t.text.trim())
              .map((t, i) => (
                <div
                  key={i}
                  className={cn(
                    "text-xs leading-relaxed",
                    t.role === "user"
                      ? "text-muted-foreground text-right"
                      : "text-foreground",
                    !t.final && "opacity-60",
                  )}
                >
                  {t.text}
                </div>
              ))}
            <div ref={transcriptEndRef} />
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="px-4 py-2 border-t border-destructive/20 bg-destructive/5 text-destructive text-xs">
          {error}
        </div>
      )}
    </div>
  );

  return createPortal(widget, document.body);
}
